import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageSeo from '@/components/PageSeo';

export const RESERVE_TERMS_VERSION = '1.0';
export const RESERVE_TERMS_EFFECTIVE_DATE = 'February 26, 2026';

export const reserveTermsSections = [
  {
    id: 'overview',
    title: '1. Program Overview',
    content: `Defera Reserve is a closed-loop stored value program that allows users to pre-fund travel-related purchases through the Defera platform.

Funds added to Reserve are converted into Defera Credits and may be used solely for eligible travel services offered through Defera.

Reserve is not:
• A bank account
• A deposit account
• A checking or savings account
• A prepaid debit card
• A money transmission service
• An investment product
• An interest-bearing account
• FDIC insured

Participation in Reserve creates a contractual right to apply Defera Credits toward eligible Defera travel services.`
  },
  {
    id: 'credits',
    title: '2. Nature of Defera Credits',
    content: `All funds added to Reserve are immediately converted into Defera Credits.

Defera Credits:
• Have no cash value
• Are non-transferable
• May not be resold
• May not be exchanged for cash
• May not be withdrawn
• Do not earn interest
• Are not a deposit or stored in a trust account unless required by law

Defera Credits may only be applied toward eligible purchases on the Defera platform.`
  },
  {
    id: 'no-withdrawals',
    title: '3. No Withdrawals',
    content: `Reserve balances cannot be:
• Withdrawn
• Transferred
• Redeemed for cash
• Returned to the original payment method

Except where required by applicable law.

By funding Reserve, you acknowledge and agree that all amounts convert to non-withdrawable Defera Credits.`
  },
  {
    id: 'adding-funds',
    title: '4. Adding Funds',
    content: `Funds may be added using approved payment methods processed by authorized third-party payment processors.

Defera Credits are applied only after successful authorization and settlement of payment.

Defera may:
• Impose minimum or maximum funding limits
• Delay crediting funds if fraud review is required
• Reject transactions at its discretion

If a payment is reversed, disputed, or charged back, Defera may:
• Remove corresponding Credits
• Suspend Reserve access
• Cancel pending bookings`
  },
  {
    id: 'eligible-use',
    title: '5. Eligible Use of Credits',
    content: `Defera Credits may be applied toward eligible travel services including:
• Flights
• Accommodations
• Travel packages
• Service fees
• Additional travel-related offerings introduced by Defera

Defera reserves the right to determine eligible products at its sole discretion.`
  },
  {
    id: 'trip-progress',
    title: '6. Trip Progress & Tracking',
    content: `Reserve may allow users to track up to three (3) active travel itineraries at a time.

Trip Progress displays:
• Estimated live pricing
• Remaining amount needed
• Progress toward funding

Trip Progress:
• Does not reserve airfare
• Does not guarantee price
• Does not secure inventory
• Is informational only

All pricing remains dynamic until ticketed.`
  },
  {
    id: 'no-price-guarantee',
    title: '7. No Price Guarantee',
    content: `Until a booking is ticketed and confirmed:
• No airfare is held
• No price is locked
• No seat is reserved

Final booking price is determined at the time of ticket issuance.

If the current live price exceeds your Reserve balance, additional Credits must be added before ticketing.

If the current live price is lower than your Reserve balance, only the required amount will be deducted.`
  },
  {
    id: 'ticketing',
    title: '8. Ticketing',
    content: `When you elect to ticket a booking:
• Live pricing will be revalidated
• Applicable fare rules will apply
• Credits will be deducted at final confirmed amount

Unused Credits will remain in Reserve.`
  },
  {
    id: 'no-expiration',
    title: '9. No Expiration',
    content: `Defera Credits do not expire.

However, balances may be subject to applicable state unclaimed property laws if your account remains inactive for a period defined by law.

Defera may attempt to notify you before any action is taken.`
  },
  {
    id: 'accounting',
    title: '10. Accounting Treatment',
    content: `Defera Credits represent a liability of Defera until applied toward eligible services.

Credits are not deposits and do not create a fiduciary relationship.

Defera may commingle Reserve funds with operational accounts, subject to internal accounting controls.`
  },
  {
    id: 'fraud',
    title: '11. Fraud & Abuse',
    content: `Defera may suspend or terminate Reserve access if:
• Fraud is suspected
• Payment disputes occur
• Chargebacks are initiated
• Abuse of the platform is detected
• These Terms are violated

Defera may cancel bookings funded by Credits associated with fraudulent activity.`
  },
  {
    id: 'liability',
    title: '12. Limitations of Liability',
    content: `Defera is not responsible for:
• Price fluctuations
• Airline inventory changes
• Airline schedule modifications
• Fare rule restrictions
• Third-party supplier decisions

Participation in Reserve does not guarantee availability or successful booking.`
  },
  {
    id: 'modifications',
    title: '13. Program Modifications',
    content: `Defera reserves the right to:
• Modify Reserve features
• Change eligibility rules
• Limit funding amounts
• Suspend or discontinue Reserve

Continued use constitutes acceptance of modifications.`
  },
  {
    id: 'termination',
    title: '14. Termination',
    content: `Defera may terminate Reserve access at any time.

In the event of termination, unused Credits will be handled in accordance with applicable law and Defera policies.`
  },
  {
    id: 'governing-law',
    title: '15. Governing Law',
    content: `These Reserve Terms are governed by the laws of the State of Delaware, without regard to conflict-of-law principles.`
  }
];

export default function ReserveTerms() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <PageSeo path="/reserve/terms" />

      <div className="min-h-screen bg-black text-white">
        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
          <div className="max-w-4xl mx-auto px-6 md:px-8 lg:px-10 py-4 flex items-center justify-between">
            <Link to="/reserve" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Reserve</span>
            </Link>
            <Link to="/">
              <img 
                src="/defera-logo-web.png"
                alt="Defera" 
                className="h-6"
              />
            </Link>
          </div>
        </header>

        {/* Content */}
        <main className="pt-24 pb-16 px-6 md:px-8 lg:px-10">
          <div className="max-w-3xl mx-auto">
            {/* Title */}
            <div className="mb-12 text-center">
              <h1 className="text-3xl md:text-4xl font-light mb-4">
                Defera Reserve Terms & Conditions
              </h1>
              <p className="text-white/60 font-normal">
                Version {RESERVE_TERMS_VERSION} • Effective Date: {RESERVE_TERMS_EFFECTIVE_DATE}
              </p>
            </div>

            {/* Introduction */}
            <div className="mb-12 p-6 rounded-2xl bg-white/5 border border-white/10">
              <p className="text-white/80 font-normal leading-relaxed">
                These Defera Reserve Terms & Conditions ("Reserve Terms") govern your participation in the Defera Reserve program ("Reserve"), operated by Defera ("Defera," "we," "us," or "our").
              </p>
              <p className="text-white/80 font-normal leading-relaxed mt-4">
                By adding funds to, accessing, or using Reserve, you agree to these Reserve Terms in addition to Defera's general <Link to="/terms" className="text-white underline hover:text-white/80">Terms of Service</Link>.
              </p>
              <p className="text-white/80 font-normal leading-relaxed mt-4 font-medium">
                If you do not agree, you may not use Defera Reserve.
              </p>
            </div>

            {/* Sections */}
            <div className="space-y-10">
              {reserveTermsSections.map((section) => (
                <section key={section.id} id={section.id}>
                  <h2 className="text-xl font-light mb-4 text-white">
                    {section.title}
                  </h2>
                  <div className="text-white/80 font-normal leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </section>
              ))}
            </div>

            {/* Footer Note */}
            <div className="mt-16 pt-8 border-t border-white/10">
              <p className="text-white/40 text-sm font-normal text-center">
                Last updated: {RESERVE_TERMS_EFFECTIVE_DATE} • Version {RESERVE_TERMS_VERSION}
              </p>
              <p className="text-white/40 text-sm font-normal text-center mt-2">
                For questions about these terms, please <Link to="/contact" className="underline hover:text-white/60">contact us</Link>.
              </p>
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="py-8 px-6 md:px-8 lg:px-10 border-t border-white/10">
          <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <Link to="/">
              <img 
                src="/defera-logo-web.png"
                alt="Defera" 
                className="h-5"
              />
            </Link>
            <div className="flex items-center gap-6 text-sm text-white/60">
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/reserve/terms" className="hover:text-white transition-colors">Reserve Terms</Link>
            </div>
            <p className="text-sm text-white/40">
              © {new Date().getFullYear()} Defera
            </p>
          </div>
        </footer>
      </div>
    </>
  );
}
